import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { OverviewResponse, TiktokFive, TiktokMonthFacts } from '../contract';
import { COLOR_DOWN, COLOR_UP, fmtHours, fmtKc, fmtNum, fmtShare, secondsToHours } from '../format';
import { MetricRow, PctBadge } from './metric-row';
import styles from '../style.module.scss';
import more from '../style-more.module.scss';

/** 5 cột TikTok theo thứ tự file; `prevKey` = khoá trong `tiktokMonth.prev`. */
export const TIKTOK_FIVE: {
  key: keyof TiktokFive;
  prevKey: keyof TiktokMonthFacts['prev'];
  label: string;
  fmtPrev: (v: number) => string;
}[] = [
  { key: 'diamonds', prevKey: 'diamonds', label: 'Kim cương', fmtPrev: (v) => fmtKc(v) },
  {
    key: 'liveDuration',
    prevKey: 'liveSeconds',
    label: 'Giờ LIVE',
    fmtPrev: (v) => fmtHours(secondsToHours(v)),
  },
  { key: 'liveDays', prevKey: 'liveDays', label: 'Ngày hợp lệ', fmtPrev: (v) => fmtNum(v) },
  {
    key: 'newFollowers',
    prevKey: 'newFollowers',
    label: 'Follower mới',
    fmtPrev: (v) => fmtNum(v),
  },
  { key: 'liveSessions', prevKey: 'liveSessions', label: 'Phiên LIVE', fmtPrev: (v) => fmtNum(v) },
];

/** Thanh "% đã đạt" (100% = bằng tháng trước) kèm chữ. */
const AchievedBar = ({ pct }: { pct: number | null }) => {
  if (pct === null) return <span className={styles.muted}>—</span>;
  const color = pct >= 100 ? COLOR_UP : COLOR_DOWN;
  return (
    <span className={more.achieved}>
      <span className={more.barTrack} aria-hidden>
        <span style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
      </span>
      <strong style={{ color }}>{fmtShare(pct)}</strong>
    </span>
  );
};

/** Khối "Theo file tháng TikTok" ở Tổng quan (chỉ khi tháng có file tháng). */
export const TiktokMonthPanel = ({ ov }: { ov: OverviewResponse }) => {
  const t = ov.tiktokMonth;
  if (!t) return null;
  return (
    <section className={styles.card} aria-label="Theo file tháng TikTok">
      <h2 className={styles.cardTitle}>
        Theo file tháng TikTok{t.period ? ` (${t.period})` : ''}
      </h2>
      {t.full === false && (
        <p className={more.note}>File chưa trọn tháng — số của giai đoạn trong ngoặc.</p>
      )}
      <MetricRow
        label="Creator tăng KC so tháng trước"
        value={<strong style={{ color: COLOR_UP }}>▲ {fmtNum(t.creatorsUp)}</strong>}
      />
      <MetricRow
        label="Creator giảm KC so tháng trước"
        value={<strong style={{ color: COLOR_DOWN }}>▼ {fmtNum(t.creatorsDown)}</strong>}
      />
      {t.avgAchievedPct && (
        <>
          <h3 className={more.subTitle}>% đã đạt so với tháng trước (bình quân theo tổng)</h3>
          {TIKTOK_FIVE.map((m) => (
            <MetricRow
              key={m.key}
              label={m.label}
              value={<AchievedBar pct={t.avgAchievedPct ? t.avgAchievedPct[m.key] : null} />}
            />
          ))}
          <p className={more.note}>
            100% = bằng tháng trước; số do TikTok tính trong file tháng.
          </p>
        </>
      )}
    </section>
  );
};

interface TRow {
  key: string;
  label: string;
  prev: string;
  achieved: number | null;
  vsPrev: number | null;
}

/** Drawer: bảng 5 cột TikTok (tháng trước · % đã đạt · % so tháng trước). */
export const TiktokMonthTable = ({ t }: { t: TiktokMonthFacts }) => {
  const rows: TRow[] = TIKTOK_FIVE.map((m) => {
    const p = t.prev[m.prevKey];
    return {
      key: m.key,
      label: m.label,
      prev: p === null || p === undefined ? '—' : m.fmtPrev(p),
      achieved: t.achievedPct[m.key],
      vsPrev: t.vsPrevPct[m.key],
    };
  });
  const columns: ColumnsType<TRow> = [
    { title: 'Chỉ số', dataIndex: 'label' },
    { title: 'Tháng trước', dataIndex: 'prev', align: 'right' },
    {
      title: '% đã đạt',
      dataIndex: 'achieved',
      align: 'right',
      render: (v: number | null) => fmtShare(v),
    },
    {
      title: 'So tháng trước',
      dataIndex: 'vsPrev',
      render: (v: number | null) => <PctBadge pct={v} />,
    },
  ];
  return (
    <section className={styles.card} aria-label="Số liệu TikTok tháng">
      <h2 className={styles.cardTitle}>Số liệu TikTok tháng ({t.period})</h2>
      <Table<TRow>
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 420 }}
      />
    </section>
  );
};
